import { useEffect, useState } from "react";
import { DatabaseIcon, ShieldAlertIcon } from "lucide-react";

import {
  useRetentionPolicy,
  useStorageUsage,
  useUpdateRetentionPolicy,
} from "@/api/storage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryState } from "@/components/QueryState";
import { cn } from "@/lib/utils";


function gb(bytes: number): string {
  if (!bytes) return "0 GB";
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}


export default function StorageSettings() {
  const { data: policy, isLoading, isError, error, refetch } = useRetentionPolicy();
  const { data: usage } = useStorageUsage();
  const update = useUpdateRetentionPolicy();

  const [videoDays, setVideoDays] = useState<number>(1800);
  const [feedDays, setFeedDays] = useState<number>(90);
  const [auditDays, setAuditDays] = useState<number>(3650);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!policy) return;
    setVideoDays(policy.video_retention_days);
    setFeedDays(policy.feed_retention_days);
    setAuditDays(policy.audit_retention_days);
    setDeleteEnabled(policy.video_delete_enabled);
  }, [policy]);

  // T1a — `isLoading || !policy` renders "Loading…" FOREVER on a failed fetch:
  // React Query sets isLoading=false and policy=undefined, so this branch stays
  // true and nothing ever says the request failed. A spinner that never
  // resolves is the same false signal as an empty table.
  if (isError) {
    return (
      <QueryState
        isLoading={false}
        isError
        error={error}
        label="the storage policy"
        onRetry={refetch}
      >
        {null}
      </QueryState>
    );
  }
  if (isLoading || !policy) return <p className="text-sm">Loading…</p>;
  const eligible = usage?.eligibility?.eligible_count ?? 0;

  const onSave = async () => {
    await update.mutateAsync({
      video_retention_days: videoDays,
      feed_retention_days: feedDays,
      audit_retention_days: auditDays,
      video_delete_enabled: deleteEnabled,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <DatabaseIcon className="h-6 w-6" />
          Storage & Retention
        </h1>
        <p className="text-sm text-muted-foreground">
          Retention here is a <strong>compliance control</strong>, not
          a cost lever. S3 lifecycle moves data through hot → cold →
          archive automatically; this page controls when (and if) it
          is <em>deleted</em>. Legal-hold recordings and recordings
          tied to alerts / cases are exempt from deletion.
        </p>
      </div>

      <Card>
        <CardContent className="grid gap-4 p-4 md:grid-cols-3">
          <Stat label="Hot tier" value={usage?.by_tier?.hot?.count ?? 0}
                hint={gb(usage?.by_tier?.hot?.bytes ?? 0)} />
          <Stat label="Cold tier" value={usage?.by_tier?.cold?.count ?? 0}
                hint={gb(usage?.by_tier?.cold?.bytes ?? 0)} />
          <Stat label="Archive (Glacier)" value={usage?.by_tier?.glacier?.count ?? 0}
                hint={gb(usage?.by_tier?.glacier?.bytes ?? 0)} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-lg font-semibold">Retention windows</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label>Video retention (days)</Label>
              <Input
                type="number" min={1} max={3650} value={videoDays}
                onChange={(e) => setVideoDays(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <Label>Notification feed (days)</Label>
              <Input
                type="number" min={1} max={3650} value={feedDays}
                onChange={(e) => setFeedDays(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <Label>Audit log (days)</Label>
              <Input
                type="number" min={365} max={7300} value={auditDays}
                onChange={(e) => setAuditDays(Number(e.target.value))}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            BFSI defaults: video 1800 d (≈5 yr), feed 90 d, audit
            3650 d (10 yr). Audit retention bias is intentional —
            audit logs are evidence; do not prune aggressively.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-start gap-3">
            <ShieldAlertIcon className="mt-1 h-5 w-5 text-amber-600" />
            <div>
              <h2 className="text-lg font-semibold">Video deletion</h2>
              <p className="text-xs text-muted-foreground">
                Deletion is <strong>OFF by default</strong>. Turning
                this on enables the daily sweep to remove recordings
                older than the video-retention window. Recordings
                with a legal hold or attached to an alert/case are
                always exempt.
              </p>
            </div>
          </div>
          <div className="rounded border bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-medium">Dry-run preview</p>
            <p>
              {eligible} recording(s) would be deleted on the next sweep
              given the current retention window. {usage?.eligibility?.legal_hold_count ?? 0} on legal hold
              and {usage?.eligibility?.incident_flagged_count ?? 0} incident-flagged would be skipped.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={deleteEnabled}
              onChange={(e) => {
                if (e.target.checked) {
                  setConfirmDelete(true);
                } else {
                  setDeleteEnabled(false);
                  setConfirmDelete(false);
                }
              }}
            />
            Enable automatic deletion of recordings older than retention
          </label>
          {confirmDelete && !deleteEnabled && (
            <Card className="border-red-200">
              <CardContent className="space-y-2 p-3 text-xs">
                <p className="font-semibold text-red-700">
                  Confirm: enabling deletion is destructive.
                </p>
                <p>
                  Deleted recordings are not recoverable. Legal-hold
                  and incident-flagged recordings remain exempt, but
                  anything else older than {videoDays} days will be
                  removed on the next sweep.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => { setDeleteEnabled(true); setConfirmDelete(false); }}
                  >Yes, enable deletion</Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setConfirmDelete(false)}
                  >Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex items-center gap-2">
            <Button onClick={onSave}>Save policy</Button>
            {saved && (
              <Badge variant="online">Saved</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn("text-3xl font-semibold")}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
